import React, { useState, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Avatar, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import Table from '../../../commonComponents/Table';
import { TopCustomer, SortConfig, SortDirection, CustomersRankingGridProps } from './types';
import { formatDateAndTime } from '../../../utils/formatDateTime';
import { SORT_KEYS, SORT_DIRECTIONS } from './constants';
import { useTheme } from '../../../context/ThemeContext';
import { RankingCard, SectionTitle } from './styles';

const CustomersRankingGrid: React.FC<CustomersRankingGridProps> = ({ data = [], loading = false }) => {
  const { isDarkMode } = useTheme();
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: SORT_DIRECTIONS.ASC as SortDirection,
  });

  const handleSort = (columnKey: string) => {
    let direction: SortDirection = SORT_DIRECTIONS.ASC as SortDirection;
    if (sortConfig.key === columnKey && sortConfig.direction === SORT_DIRECTIONS.ASC) {
      direction = SORT_DIRECTIONS.DESC as SortDirection;
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof TopCustomer];
      let bValue: any = b[sortConfig.key as keyof TopCustomer];

      if (sortConfig.key === SORT_KEYS.NAME) {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      } else if (sortConfig.key === SORT_KEYS.RECENT_VISIT) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === SORT_DIRECTIONS.ASC ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === SORT_DIRECTIONS.ASC ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  const renderSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSortDown} style={{ marginLeft: '8px', opacity: 0.3 }} />;
    }
    return sortConfig.direction === SORT_DIRECTIONS.ASC ? (
      <FontAwesomeIcon icon={faSortUp} style={{ marginLeft: '8px' }} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} style={{ marginLeft: '8px' }} />
    );
  };

  const columns: ColumnsType<TopCustomer> = [
    {
      title: (
        <span onClick={() => handleSort(SORT_KEYS.NAME)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          Customer {renderSortIcon(SORT_KEYS.NAME)}
        </span>
      ),
      key: 'customer',
      render: (_, record) => (
        <Space>
          <Avatar size="small">{record.name.charAt(0)}</Avatar>
          <span>{record.name}</span>
        </Space>
      ),
    },
    {
      title: (
        <span onClick={() => handleSort(SORT_KEYS.VISITS)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          Visits {renderSortIcon(SORT_KEYS.VISITS)}
        </span>
      ),
      key: 'visits',
      align: 'right',
      render: (_, record) => record.visits,
    },
    {
      title: (
        <span onClick={() => handleSort(SORT_KEYS.RECENT_VISIT)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          Recent Visit {renderSortIcon(SORT_KEYS.RECENT_VISIT)}
        </span>
      ),
      key: 'recentVisit',
      align: 'right',
      render: (_, record) => {
        const formatted = formatDateAndTime(record.recentVisit);
        return `${formatted.formattedDate} ${formatted.formattedTime}`;
      },
    },
    {
      title: (
        <span onClick={() => handleSort(SORT_KEYS.TOTAL_SPENT)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          Total Spend ($) {renderSortIcon(SORT_KEYS.TOTAL_SPENT)}
        </span>
      ),
      key: 'totalSpent',
      align: 'right',
      render: (_, record) => `$${(record.totalSpent || 0).toFixed(2)}`,
    },
  ];

  return (
    <RankingCard>
      <SectionTitle level={4}>Top 5 Customers</SectionTitle>
      <Table<TopCustomer>
        columns={columns}
        dataSource={sortedData}
        rowKey="id"
        loading={loading}
        pagination={false}
        emptyText="No customers found"
        scroll={{ x: true }}
        headerBackgroundColor={isDarkMode ? '#2D3748' : '#F7FAFC'}
        removeVerticalBorders={true}
        thPadding="16px 12px"
        tdPadding="16px 12px"
        firstChildBorderRadius="8px"
        border="0"
        borderBottomLeftRight={true}
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </RankingCard>
  );
};

export default CustomersRankingGrid;
